import type { QuoteRequestRecipientRow, SendReadiness } from "@kollectivia/shared";
import { useCallback, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { PreparedAttachment } from "../../../components/PreparedAttachment";
import { ApiException } from "../../../lib/apiError";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useInsurerCatalog } from "../../../lib/queries/insurers";
import { useUpdateProject } from "../../../lib/queries/projects";
import {
  useUpdateQuoteRequest,
  useUpdateQuoteRequestRecipient,
} from "../../../lib/queries/quoteRequests";

/** Explicit outgoing data only. Internal notes and PDF answers never enter this component. */
export function DeliveryPreparation({
  requestId,
  projectId,
  delivery,
  recipients,
  editable,
  onDownload,
  onDirtyChange,
}: {
  requestId: string;
  projectId: string;
  delivery: NonNullable<SendReadiness["delivery"]>;
  recipients: readonly QuoteRequestRecipientRow[];
  editable: boolean;
  onDownload: (id: string) => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const { labelFor } = useInsurerCatalog();
  const updateProject = useUpdateProject(projectId);
  const updateRequest = useUpdateQuoteRequest();
  const updateRecipient = useUpdateQuoteRequestRecipient();
  const [dirtyFields, setDirtyFields] = useState<ReadonlySet<string>>(new Set());
  const fieldDirty = useCallback((field: string, dirty: boolean) => {
    setDirtyFields((previous) => {
      if (previous.has(field) === dirty) return previous;
      const next = new Set(previous);
      if (dirty) next.add(field);
      else next.delete(field);
      return next;
    });
  }, []);
  useEffect(() => {
    onDirtyChange(dirtyFields.size > 0);
  }, [dirtyFields, onDirtyChange]);
  return (
    <section
      className="space-y-4 rounded-xl border border-slate-200 bg-white p-5"
      aria-label={t("delivery.title")}
    >
      <h2 className="text-lg font-semibold text-slate-900">{t("delivery.title")}</h2>
      <p className="font-medium text-slate-900">{delivery.company_name}</p>
      <p className="text-sm text-slate-600">{t("delivery.help")}</p>
      <DeliveryField
        key={`commission:${delivery.requested_commission_rate}`}
        label={t("delivery.commission")}
        fieldId="commission"
        onDirtyChange={fieldDirty}
        value={delivery.requested_commission_rate?.toString() ?? ""}
        numeric
        editable={editable}
        onSave={async (value) => {
          await updateProject.mutateAsync({
            requested_commission_rate: value === "" ? null : Number(value),
          });
        }}
      />
      <DeliveryField
        key={`common:${delivery.insurer_email_note}`}
        label={t("delivery.common_note")}
        fieldId="common"
        onDirtyChange={fieldDirty}
        value={delivery.insurer_email_note ?? ""}
        maxLength={4000}
        editable={editable}
        onSave={async (value) => {
          await updateRequest.mutateAsync({
            quoteRequestId: requestId,
            input: { insurer_email_note: value || null },
          });
        }}
      />
      {recipients.map((recipient) => (
        <DeliveryField
          key={`${recipient.id}:${recipient.insurer_email_note}`}
          fieldId={recipient.id}
          onDirtyChange={fieldDirty}
          label={t("delivery.recipient_note", {
            insurer: `${labelFor(recipient.insurer_code)} — ${recipient.recipient_email ?? t("delivery.email_preview.no_address")}`,
          })}
          value={recipient.insurer_email_note ?? ""}
          maxLength={2000}
          editable={editable && recipient.status === "pending"}
          onSave={async (value) => {
            await updateRecipient.mutateAsync({
              quoteRequestId: requestId,
              recipientId: recipient.id,
              input: { insurer_email_note: value || null },
            });
          }}
        />
      ))}
      <div className="border-t border-slate-200 pt-4">
        <h3 className="font-medium text-slate-900">{t("delivery.attachments")}</h3>
        {delivery.attachments.length === 0 ? (
          <p className="text-sm text-slate-600">{t("delivery.no_attachments")}</p>
        ) : (
          <ul className="space-y-1">
            {delivery.attachments.map((file) => (
              <li key={file.file_id}>
                <PreparedAttachment file={file} onDownload={onDownload} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function DeliveryField({
  label,
  value,
  numeric = false,
  maxLength,
  editable,
  onSave,
  fieldId,
  onDirtyChange,
}: {
  label: string;
  value: string;
  numeric?: boolean;
  maxLength?: number;
  editable: boolean;
  onSave: (value: string) => Promise<void>;
  fieldId: string;
  onDirtyChange: (field: string, dirty: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const dirty = draft !== value && !saved;
  useEffect(() => {
    onDirtyChange(fieldId, dirty || saving);
    return () => onDirtyChange(fieldId, false);
  }, [fieldId, dirty, saving, onDirtyChange]);

  const change = (text: string) => {
    setDraft(text);
    setSaved(false);
  };
  return (
    <form
      className="space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (!editable || saving) return;
        setSaving(true);
        setError(null);
        setSaved(false);
        void onSave(draft)
          .then(() => setSaved(true))
          .catch((err: unknown) => {
            setError(
              err instanceof ApiException
                ? getApiErrorMessage(err, normalizeLang(i18n.language)) || t("errors.generic")
                : t("errors.generic"),
            );
          })
          .finally(() => setSaving(false));
      }}
    >
      <label className="label [overflow-wrap:anywhere]" htmlFor={id}>
        {label}
      </label>
      {numeric ? (
        <input
          id={id}
          name="requested_commission_rate"
          className="input"
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step="0.01"
          value={draft}
          disabled={!editable || saving}
          onChange={(event) => change(event.target.value)}
        />
      ) : (
        <textarea
          id={id}
          name="insurer_email_note"
          className="input min-h-24"
          maxLength={maxLength}
          value={draft}
          disabled={!editable || saving}
          onChange={(event) => change(event.target.value)}
        />
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {saved && (
        <p role="status" className="text-sm text-emerald-700">
          {t("delivery.saved")}
        </p>
      )}
      {editable && (
        <button
          type="submit"
          className="btn-primary min-h-11"
          disabled={!dirty || saving}
          aria-busy={saving}
        >
          {t("delivery.save")}
        </button>
      )}
    </form>
  );
}
